// src/components/tasks/CreateTaskModal.js
import React, { useMemo, useRef, useState } from "react";
import api, { uploadTaskAttachments } from "../../api/axiosConfig";
import { FaTimes } from "react-icons/fa";
import RichTextEditor from "../common/RichTextEditor";

/* ---- Attachment Input mit Drag & Drop + Clipboard ---- */
function AttachmentInput({ value = [], onChange }) {
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const handleFiles = (files) => {
    const newFiles = Array.from(files);
    onChange([...value, ...newFiles]);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    
    const files = e.dataTransfer?.files;
    if (files?.length) {
      handleFiles(files);
    }
  };

  const onPaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files = [];
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it.kind === "file") {
        const f = it.getAsFile();
        if (f) files.push(f);
      }
    }
    if (files.length) {
      e.preventDefault();
      handleFiles(files);
    }
  };

  const removeFile = (index) => {
    onChange(value.filter((_, i) => i !== index));
  };

  // Globaler Paste-Handler für das gesamte Document
  React.useEffect(() => {
    const handleGlobalPaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const files = [];
      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        if (it.kind === "file") {
          const f = it.getAsFile();
          if (f) files.push(f);
        }
      }
      if (files.length) {
        // Nur verarbeiten wenn der Modal aktiv ist
        e.preventDefault();
        handleFiles(files);
      }
    };

    document.addEventListener("paste", handleGlobalPaste);
    return () => document.removeEventListener("paste", handleGlobalPaste);
  }, [value]);

  return (
    <div className="field">
      <span className="field-label">Anhänge (Drag & Drop, Clipboard oder Auswahl)</span>

      <div
        onClick={() => fileRef.current?.click()}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={`dropzone ${dragOver ? "is-dragover" : ""}`}
      >
        <div className="dropzone-title">
          📁 Dateien hierher ziehen oder klicken zum Durchsuchen
        </div>
        <div className="dropzone-hint">
          Du kannst auch mit Strg+V Bilder aus der Zwischenablage einfügen
        </div>
      </div>

      <input
        ref={fileRef}
        hidden
        type="file"
        multiple
        onChange={(e) => {
          handleFiles(e.target.files || []);
          e.target.value = "";
        }}
      />

      {!!value.length && (
        <div className="file-list">
          <div className="file-list-title">
            {value.length} Datei(en) ausgewählt:
          </div>
          {value.map((file, idx) => (
            <div key={idx} className="file-row">
              <span>📄 {file.name}</span>
              <button
                type="button"
                className="btn btn-danger-ghost btn-sm"
                onClick={() => removeFile(idx)}
              >
                Entfernen
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---- Labels Chips ---- */
function LabelsInput({ value = [], onChange }) {
  const [input, setInput] = useState("");
  const add = () => {
    const v = input.trim();
    if (!v) return;
    onChange([...new Set([...(value || []), v])]);
    setInput("");
  };
  const remove = (lbl) => onChange((value || []).filter((x) => x !== lbl));
  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      add();
    }
    if (e.key === "Backspace" && !input && value?.length)
      remove(value[value.length - 1]);
  };
  return (
    <div>
      <div className="label-chips">
        {(value || []).map((lbl) => (
          <span key={lbl} className="label-chip">
            {lbl}
            <button
              type="button"
              onClick={() => remove(lbl)}
              aria-label={`Label ${lbl} entfernen`}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <div className="inline-form">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Neues Label… (Enter oder ,)"
          className="input"
        />
        <button type="button" className="btn btn-secondary" onClick={add}>
          Hinzufügen
        </button>
      </div>
    </div>
  );
}

/* ---- Hauptkomponente ---- */
export default function CreateTaskModal({
  isOpen,
  onClose,
  boardId,
  statuses = [],
  members = [],
  defaultStatus,
  onCreated,
}) {
  const [title, setTitle] = useState("");
  const [status, setStatus] = useState(defaultStatus || statuses?.[0] || "");
  // WICHTIG: null = niemand (wie im TaskItem-Flow)
  const [assigneeId, setAssigneeId] = useState(null);
  const [priority, setPriority] = useState(0);
  const [labels, setLabels] = useState([]);
  const [detailsHtml, setDetailsHtml] = useState(""); // HTML vom RichTextEditor
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const canSubmit = useMemo(
    () => title.trim().length > 0 && status,
    [title, status]
  );

  const reset = () => {
    setTitle("");
    setStatus(defaultStatus || statuses?.[0] || "");
    setAssigneeId(null); // zurück auf niemand
    setPriority(0);
    setLabels([]);
    setDetailsHtml("");
    setFiles([]);
    setError("");
  };
  const close = () => {
    if (!submitting) {
      reset();
      onClose?.();
    }
  };

  /* ---- Submit ---- */
  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        title: title.trim(),
        status,
        assignee: assigneeId,            // bereits null oder number
        details: detailsHtml,            // HTML aus dem RTE
        priority: Number(priority) || 0,
        labels: Array.isArray(labels) ? labels : [],
      };

      const { data: created } = await api.post(
        `/boards/${boardId}/tasks`,
        payload
      );

      if (files.length) await uploadTaskAttachments(created.id, files);
      onCreated?.({ ...created });
      close();
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || "Erstellen fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // Defensive ID-Ermittlung wie in TaskItem-Ansätzen
  const getMemberId = (m) => m.id ?? m.user_id ?? m.userId ?? m.uid;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="ctm-title"
      className="ctm-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <form
        onSubmit={submit}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        className="ctm-card"
      >
        <header className="ctm-header">
          <h2 id="ctm-title">Neue Aufgabe erstellen</h2>
          <button type="button" className="icon-btn" onClick={close} aria-label="Schließen">
            <FaTimes />
          </button>
        </header>

        <div className="ctm-body">
          {error && <div className="form-error" role="alert">{error}</div>}

          <label className="field">
            <span className="field-label">Titel *</span>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              placeholder="Kurzer, prägnanter Titel"
              className="input"
              autoFocus
            />
          </label>

          {/* Status / Bearbeiter / Priorität */}
          <div className="ctm-grid-3">
            <label className="field">
              <span className="field-label">Status *</span>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="input"
              >
                {statuses.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>

            <label className="field">
              <span className="field-label">Bearbeiter</span>
              <select
                // Select arbeitet immer mit Strings; null wird zu ""
                value={assigneeId == null ? "" : String(assigneeId)}
                onChange={(e) => {
                  const v = e.target.value; // immer String
                  setAssigneeId(v === "" ? null : Number(v));
                }}
                className="input"
              >
                <option value="">— niemand —</option>
                {members.map((m) => {
                  const id = getMemberId(m);
                  return (
                    <option key={id} value={String(id)}>
                      {m.name || m.email || id}
                    </option>
                  );
                })}
              </select>
            </label>

            <label className="field">
              <span className="field-label">Priorität</span>
              <select
                value={priority}
                onChange={(e) => setPriority(Number(e.target.value))}
                className="input"
              >
                {["Low", "Medium", "High", "Highest"].map((t, i) => (
                  <option key={i} value={i}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {/* Labels */}
          <div className="field">
            <span className="field-label">Labels</span>
            <LabelsInput value={labels} onChange={setLabels} />
          </div>

          {/* Beschreibung */}
          <div className="field">
            <span className="field-label">Beschreibung</span>

            <RichTextEditor
              mode="full"
              value={detailsHtml}
              onChange={setDetailsHtml}
              placeholder="Schreibe in einfachem Rich-Text. Du kannst fett/kursiv/Listen/Überschriften und Links nutzen."
            />
          </div>
          {/* Anhänge */}
          <AttachmentInput value={files} onChange={setFiles} />
        </div>

        <footer className="ctm-footer">
          <button type="button" className="btn btn-secondary" onClick={close} disabled={submitting}>
            Abbrechen
          </button>
          <button type="submit" className="btn" disabled={!canSubmit || submitting}>
            {submitting ? "Erstellt…" : "Erstellen"}
          </button>
        </footer>
      </form>
    </div>
  );
}
